import type { FormValues } from '../types';

const ACTIVATION_FIELDS: Record<string, string> = {
  qtdEANsDistintos: 'Quantidade de EANs distintos para ativação da promo',
  familiaObrigatoria: 'Família obrigatória para ativação?',
  qtdMinimaAtivacao: 'Quantidade mínima para ativar a promo',
};

function familySuffixes(values: FormValues): string[] {
  const count = values.numeroCard === '1' && !Array.isArray(values.qtdCardsFilhos)
    ? Math.min(15, Math.max(0, parseInt(String(values.qtdCardsFilhos ?? '').replace(/[^0-9]/g, ''), 10) || 0))
    : 0;
  return ['', ...Array.from({ length: count }, (_, index) => '__child' + (index + 2))];
}

function scalar(value: FormValues[string] | undefined): string {
  return Array.isArray(value) ? '' : String(value ?? '').trim();
}

function percentage(value: FormValues[string] | undefined): number | null {
  const text = scalar(value);
  if (!/^\d+(?:[.,]\d+)?$/.test(text)) return null;
  const parsed = Number(text.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100 ? parsed : null;
}

// Explicit blanks prevent the XLSX child fallback from restoring incompatible parent values.
export function normalizeCompreEGanheValues(templateId: string, values: FormValues): FormValues {
  if (templateId !== 't3') return values;
  const normalized = { ...values };
  Object.keys(normalized).filter((key) => /^(?:skuCombo|sku_combo)(?:__child\d+)?$/.test(key)).forEach((key) => {
    delete normalized[key];
  });
  Object.keys(values).filter((key) => /^tipoFamilia(?:__child\d+)?$/.test(key)).forEach((key) => {
    const suffix = key.slice('tipoFamilia'.length);
    const type = scalar(values[key]);
    if (type === '1') normalized['valorDesconto' + suffix] = '';
    if (type === '2') {
      Object.keys(ACTIVATION_FIELDS).forEach((field) => { normalized[field + suffix] = ''; });
    }
  });
  return normalized;
}

export function validateCompreEGanheValues(templateId: string, values: FormValues): Record<string, string> {
  if (templateId !== 't3') return {};
  const errors: Record<string, string> = {};
  const discounts: Array<{ key: string; value: number }> = [];

  familySuffixes(values).forEach((suffix) => {
    const type = scalar(values['tipoFamilia' + suffix]);
    if (!['1', '2', '3'].includes(type)) {
      errors['tipoFamilia' + suffix] = 'Selecione o tipo de família.';
      return;
    }
    if (type === '1' || type === '3') {
      Object.entries(ACTIVATION_FIELDS).forEach(([field, label]) => {
        if (!scalar(values[field + suffix])) {
          errors[field + suffix] = label + ' é obrigatório para famílias do tipo Requisito ou Ambos.';
        }
      });
    }
    if (type === '2' || type === '3') {
      const key = 'valorDesconto' + suffix;
      const value = percentage(values[key]);
      if (value === null) {
        errors[key] = 'Informe o valor do desconto entre 0 e 100, sem o símbolo %, para famílias do tipo Benefício ou Ambos.';
      } else {
        discounts.push({ key, value });
      }
    }
  });

  if (discounts.some((discount) => discount.value !== discounts[0].value)) {
    discounts.forEach(({ key }) => {
      errors[key] = 'O desconto deve ser igual em todas as famílias do tipo Benefício ou Ambos desta promoção.';
    });
  }
  return { ...errors, ...getCompreEGanheTitleErrors(templateId, values) };
}

// Only explicit percentages are compared; other numbers and titles without % remain free text.
export function getCompreEGanheTitleErrors(templateId: string, values: FormValues): Record<string, string> {
  if (templateId !== 't3') return {};
  const discounts = familySuffixes(values).flatMap((suffix) => {
    if (!['2', '3'].includes(scalar(values['tipoFamilia' + suffix]))) return [];
    const value = percentage(values['valorDesconto' + suffix]);
    return value === null ? [] : [value];
  });
  if (!discounts.length) return {};

  const errors: Record<string, string> = {};
  ['nomeExibicao', 'nomePromo'].forEach((key) => {
    const title = scalar(values[key]);
    const percentages = Array.from(title.matchAll(/(\d+(?:[.,]\d+)?)\s*%/g))
      .map((match) => Number(match[1].replace(',', '.')));
    if (!percentages.some((value) => discounts.some((discount) => value !== discount))) return;
    errors[key] = 'Corrija o título “' + title + '”: o percentual deve ser igual ao desconto das famílias Benefício/Ambos (' + Array.from(new Set(discounts)).join('%, ') + '%).';
  });
  return errors;
}
